import { useState, useEffect, type RefObject } from 'react';
import { Flex, Spinner } from '@chakra-ui/react';

interface VideoSectionProps {
  videoRef: RefObject<HTMLVideoElement>;
  videoUrl: string;
  handleTime: (currentTime: number) => void;
  timeJumpValue?: number;
  mode?: 'mini' | 'modal';
}

export const VideoSection = ({
  videoRef = null,
  videoUrl = '',
  handleTime = () => {},
  timeJumpValue = 0,
  mode = 'mini',
}: VideoSectionProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleTimeUpdate = (): void => {
    handleTime(videoRef?.current?.currentTime || 0);
  };

  useEffect(() => {
    if (timeJumpValue && videoRef.current) {
      videoRef.current.currentTime = timeJumpValue / 1000;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing; behavior preserved (extraction move)
      setIsLoading(true);
    }
  }, [timeJumpValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const video = videoRef?.current;

    const handleCanPlayThrough = (): void => {
      setIsLoading(false);
    };

    video?.addEventListener('canplaythrough', handleCanPlayThrough);

    return () => {
      video?.removeEventListener('canplaythrough', handleCanPlayThrough);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      pos="relative"
      bg="#000"
      borderRadius={mode === 'mini' ? 'xs' : 'lg'}
      w={mode === 'modal' ? '100%' : undefined}
      h={mode === 'modal' ? '100%' : undefined}
    >
      {isLoading && <Spinner size="sm" color="#605AFB" pos="absolute" />}
      <video
        ref={videoRef}
        onTimeUpdate={handleTimeUpdate}
        width={mode === 'modal' ? '100%' : '100%'}
        height={mode === 'modal' ? '100%' : '100%'}
        style={{
          borderRadius: mode === 'mini' ? '8px' : '16px',
          maxHeight: mode === 'modal' ? '70vh' : '110px',
          objectFit: 'contain',
        }}
        disablePictureInPicture
        controlsList="nodownload noplaybackrate"
        className={
          mode === 'mini' ? 'interview-mini-player' : 'interview-modal-player'
        }
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </Flex>
  );
};
